import React, { useState, useEffect, useCallback, useRef } from 'react'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Autocomplete from '@mui/material/Autocomplete'

import { TagMenuProps } from 'types/TextAnalysis'
import {
  PaperWrapper,
  ListItem,
  MouseEvent,
  OptionProps,
  renderPopperComponent,
  renderTagsInput,
  renderCreateTagOption,
  renderListItemContent,
  renderChipsList,
} from './Helper'
import {
  filterOptions,
  getTagLabel,
  mapTagsToLabels,
  updateTagItem,
  validateTagMenuTags,
} from './TagMenuUtils'
import { TagHelpText, PopperModifiers } from './TagComponents'
import { Tag } from 'containers/TextAnalysis/Types'

const TagMenu = ({
  anchorEl,
  fetching,
  tags,
  tagLabel,
  label = '',
  placeholder = '',
  contributionTags,
  open,
  onCloseTagMenu,
  onAddTagToContribution,
  onTagDelete,
  placement = 'right-start',
  handleKeyDown,
  showHelpText = false,
  allowCreateTag = true,
}: TagMenuProps) => {
  const [inputValue, setInputValue] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [tagsChangedStatus, setTagsChangedStatus] = useState<boolean>(false)
  const prevSelectedTags = useRef<string[]>([])

  useEffect(() => {
    setSelectedTags(contributionTags)
    prevSelectedTags.current = contributionTags
  }, [contributionTags])

  const onChange = useCallback(
    (
      _: any,
      tags: (string | OptionProps)[] | any,
      reason: string,
      detail: any,
    ) => {
      const tag = detail.option
      if (reason === 'removeOption') {
        onChipDelete(tag)
      } else if (tag?.createTag) {
        const labels = mapTagsToLabels(tags, tagLabel)
        setSelectedTags(labels)
        setTagsChangedStatus(true)
        onAddTagToContribution('new', tag.title)
      } else if (tag[tagLabel]) {
        handleClick(tag, _)
      }
    },
    [setSelectedTags, onAddTagToContribution],
  )

  const updateTags = useCallback((tag: string | Tag) => {
    setSelectedTags((prev) => {
      const newTags = updateTagItem(prev, tag)
      setTagsChangedStatus(true)
      prevSelectedTags.current = newTags
      return newTags
    })
  }, [])

  const onChipDelete = useCallback(
    (tag: string | Tag) => {
      updateTags(tag)
      onTagDelete(tag)
    },
    [updateTags, onTagDelete],
  )

  const handleClick = useCallback(
    (tag: any, e: MouseEvent) => {
      updateTags(tag)
      onAddTagToContribution('existing', tag)
      setInputValue('')
      e.stopPropagation()
    },
    [updateTags, onAddTagToContribution],
  )

  const handleInputChange = (value: string) => {
    validateTagMenuTags(value, setInputValue)
  }

  const keyDownHandler = (e: React.KeyboardEvent) => {
    if (handleKeyDown) {
      handleKeyDown(e, selectedTags, tagsChangedStatus)
      setTagsChangedStatus(false) // Reset tagsChangedStatus when closing
    }
  }

  const handleCloseTagMenu = () => {
    onCloseTagMenu(selectedTags, tagsChangedStatus)
    setInputValue('')
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={PopperModifiers()}
    >
      <ClickAwayListener onClickAway={handleCloseTagMenu}>
        <PaperWrapper elevation={2}>
          {showHelpText && <TagHelpText />}
          <Autocomplete
            inputValue={inputValue}
            id="tags-autocomplete"
            multiple
            size="small"
            freeSolo
            open
            disableClearable
            disablePortal
            value={selectedTags}
            onChange={onChange}
            PopperComponent={renderPopperComponent}
            options={tags}
            onInputChange={(_, value) => handleInputChange(value)}
            filterOptions={(options, params) =>
              filterOptions(
                options,
                params,
                tagLabel,
                selectedTags,
                allowCreateTag,
              )
            }
            getOptionLabel={(option) => getTagLabel(option, tagLabel)}
            isOptionEqualToValue={(option: any, value: any) =>
              typeof option === 'string'
                ? option === value
                : option?.[tagLabel] === value?.[tagLabel]
            }
            renderTags={(value) =>
              renderChipsList(value, onChipDelete, tagLabel)
            }
            renderInput={(params) =>
              renderTagsInput(params, label, placeholder)
            }
            renderOption={(props: any, option: string | OptionProps) => {
              const label = getTagLabel(option, tagLabel)

              if (typeof option !== 'string' && option.createTag) {
                return renderCreateTagOption(props, option)
              }
              return (
                <ListItem
                  {...props}
                  key={props.id}
                  onClick={(e: MouseEvent) => handleClick(option, e)}
                >
                  {renderListItemContent(label)}
                </ListItem>
              )
            }}
            loading
            loadingText={
              fetching && tags.length === 0 ? 'Loading...' : 'No tags available'
            }
            onKeyDown={keyDownHandler}
            autoHighlight
          />
        </PaperWrapper>
      </ClickAwayListener>
    </Popper>
  )
}

TagMenu.displayName = 'TagMenu'

export default React.memo(TagMenu)
